import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OptionComponent from './OptionComponent';
import CheckCircleIcon from './CheckCircleIcon';
import OneBallotResource from '../resources/Ballot/One';
import ballotDisplayOrder from '../resources/Ballot/constants/ballotDisplayOrder';
import ballotOptionTypeOrder from '../resources/Ballot/constants/ballotOptionTypeOrder';
import { updateBallotMutation } from '../resources/Ballot/mutations';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import ObjectiveStateResource from '../resources/ObjectiveState';
import { makeClassName } from '@siteless/util';
import useCopy from '../util/useCopy';
import { motion } from 'motion/react';
import { useGesture } from '@use-gesture/react';
import { useElementDimensions } from 'hooks';
import { moviePosterAspectRatio } from '../config/constants';
import { Header } from './Header';
import ChevronDownIcon from './ChevronDownIcon';
import { ComposeBallotFocusCategoryItem } from './ComposeBallotFocusCategoryItem';
import AllLeaguesForUserResource from '../resources/League/AllForUser';
import { CurrentUserMustResource } from '../resources/Auth/CurrentUserMust';

export let ComposeBallotFocusCategory = ({
  ballotItemKey,
  nextIncompleteCategoryKey,
  onNavigateToNextIncomplete,
  onNavigateBack,
}: {
  ballotItemKey: (typeof ballotDisplayOrder)[number];
  nextIncompleteCategoryKey?: (typeof ballotDisplayOrder)[number];
  onNavigateToNextIncomplete: () => void;
  onNavigateBack?: () => void;
}) => {
  let fallbackDivElement = document.createElement('div');
  let copy = useCopy();
  let currentUser = CurrentUserMustResource.use();
  let objectiveState = ObjectiveStateResource.use();
  let { ballotShape } = objectiveState;
  let ballot = OneBallotResource.use(currentUser.id);
  let userLeagues = AllLeaguesForUserResource.use(currentUser.id);

  let { displayName, options } = ballotShape[ballotItemKey];

  let isStrictModeRelevant = useMemo(
    () =>
      Boolean(
        Object.values(options).find(
          (item) => item.type === optionTypes.winner.key,
        ),
      ),
    [options],
  );

  let strictModeUserLeagueNames = useMemo(
    () =>
      isStrictModeRelevant
        ? userLeagues
            .filter((league) => league.strictMode)
            .map((league) => league.name)
        : [],
    [isStrictModeRelevant, userLeagues],
  );

  let nextIncomplete = nextIncompleteCategoryKey
    ? ballotShape[nextIncompleteCategoryKey]
    : null;

  let optionKeys = useMemo(
    () =>
      Object.entries(options)
        .sort(
          (a, b) =>
            ballotOptionTypeOrder.findIndex((item) => item === a[1].type) -
            ballotOptionTypeOrder.findIndex((item) => item === b[1].type),
        )
        .map(([key]) => key),
    [options],
  );

  let [focusedOptionKey, setFocusedOptionKey] = useState(optionKeys[0]);
  let focusedOptionKeyIndex = optionKeys.findIndex(
    (item) => item === focusedOptionKey,
  );

  let [scrollContainerElement, setScrollContainerElement] =
    useState<HTMLDivElement | null>(null);

  let scrollContainerDimensions = useElementDimensions(
    scrollContainerElement || fallbackDivElement,
  );

  let itemWidth = 120;
  let itemFocusedWidth = 152;

  let scrollContainerHorizontalPadding =
    (scrollContainerDimensions.width - itemWidth) / 2;

  let calculateActiveElementIndex = useCallback(
    (x: number, direction: number) => {
      let buffer = direction > 0 ? itemWidth * 0.6 : itemWidth * 0.4;

      let newFocusedElementIndex = Math.min(
        optionKeys.length - 1,
        Math.max(0, Math.floor((x + buffer) / itemWidth)),
      );
      setFocusedOptionKey(optionKeys[newFocusedElementIndex]);

      let didChange = newFocusedElementIndex !== focusedOptionKeyIndex;

      return { didChange };
    },
    [focusedOptionKeyIndex, itemWidth, optionKeys],
  );

  let focusedOptionKeyRef = useRef(focusedOptionKey);

  useEffect(() => {
    focusedOptionKeyRef.current = focusedOptionKey;
  }, [focusedOptionKey]);

  useGesture(
    {
      onScroll: ({ offset: [x], direction: [directionX] }) => {
        if (directionX === 0) return;

        let { didChange } = calculateActiveElementIndex(x, directionX);
        if (!didChange) return;
        if (!scrollContainerElement) return;
      },
    },
    {
      target: scrollContainerElement || fallbackDivElement,
      scroll: { axis: 'x', passive: false },
    },
  );

  let selectedOptionKey = ballot?.[ballotItemKey];

  let activeOptionComponentProps = useMemo(() => {
    let { data, type: typeKey } = options[focusedOptionKey];
    let type = optionTypes[typeKey];
    let optionComponentProps = type.getOptionComponentProps({
      // @ts-expect-error TODO
      data,
      copy,
      objectiveState,
    });
    let isSelected = selectedOptionKey === focusedOptionKey;

    return { ...optionComponentProps, type, isSelected };
  }, [focusedOptionKey, options, selectedOptionKey, copy, objectiveState]);

  // scroll the selected option element into view on change
  useEffect(() => {
    if (scrollContainerElement === null) return;
    if (!selectedOptionKey) return;
    let selectedIndex = optionKeys.findIndex(
      (item) => item === selectedOptionKey,
    );
    let offsetX = itemWidth * selectedIndex;
    scrollContainerElement.scrollLeft = offsetX;
  }, [
    selectedOptionKey,
    scrollContainerElement,
    optionKeys,
    scrollContainerHorizontalPadding,
    itemWidth,
  ]);

  let clampedTransition = {
    type: 'tween' as const,
    ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    duration: 0.2,
  };

  let hasSelection = Boolean(ballot?.[ballotItemKey]);
  let highlightNextIncomplete = hasSelection;

  return (
    <div className="flex h-full flex-col pt-12">
      <Header className="px-4 text-center">{displayName}</Header>
      <div
        className="flex-1 py-4"
        ref={(element) => {
          setScrollContainerElement(element);
        }}
        style={{
          // enforce smooth programmatic scrolls https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
          touchAction: 'pan-x',
          display: 'flex',
          overflow: 'auto',
          paddingLeft: scrollContainerHorizontalPadding,
          paddingRight: scrollContainerHorizontalPadding,
          alignItems: 'center',
          height: itemFocusedWidth * moviePosterAspectRatio,
        }}
      >
        {optionKeys.map((optionKey, optionKeyIndex) => {
          let { data, type: typeKey } = options[optionKey as keyof typeof options];
          let type = optionTypes[typeKey];
          let isSelected = ballot?.[ballotItemKey] === optionKey;
          let isFocused = optionKeyIndex === focusedOptionKeyIndex;

          let optionComponentProps = type.getOptionComponentProps({
            // @ts-expect-error TODO
            data,
            copy,
            objectiveState,
          });

          let nudgeBy = (itemFocusedWidth - itemWidth) / 2;
          let nudgeByModifier =
            optionKeyIndex < focusedOptionKeyIndex
              ? -1
              : optionKeyIndex > focusedOptionKeyIndex
              ? 1
              : 0;

          let translateX = nudgeBy * nudgeByModifier;

          let width = itemWidth;

          return (
            <motion.div
              onClick={() => {
                updateBallotMutation({
                  optionKey: isSelected ? '' : optionKey,
                  ballotItemKey,
                  userId: currentUser.id,
                });
              }}
              key={optionKey}
              animate={{
                scale: isFocused ? itemFocusedWidth / itemWidth : 1,
              }}
              transition={clampedTransition}
              style={{
                flex: '0 0 auto',
                scrollSnapAlign: 'center',
                width,
                height: itemWidth * moviePosterAspectRatio,
                padding: 4,
              }}
            >
              <div
                className={makeClassName(
                  'relative h-full w-full rounded-md border-4',
                  isSelected ? 'border-rose-bright' : 'border-neutral-200',
                  hasSelection && !isSelected ? 'opacity-70' : 'opacity-100',
                )}
                style={{
                  transform: `translateX(${translateX}px)`,
                  borderRadius: 8,
                  padding: 2,
                }}
              >
                <ComposeBallotFocusCategoryItem
                  type={type}
                  strictModeUserLeagueNames={strictModeUserLeagueNames}
                  isSelected={isSelected}
                  {...optionComponentProps}
                />
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mx-3 h-32">
        <OptionComponent
          onClick={() => {
            updateBallotMutation({
              optionKey: activeOptionComponentProps.isSelected
                ? ''
                : focusedOptionKey,
              ballotItemKey: ballotItemKey,
              userId: currentUser.id,
            });
          }}
          {...activeOptionComponentProps}
        />
      </div>
      {Boolean(nextIncomplete) ? (
        <div
          onClick={
            highlightNextIncomplete ? onNavigateToNextIncomplete : undefined
          }
          className={makeClassName(
            'flex h-24 flex-col items-center justify-center font-medium',
            highlightNextIncomplete ? 'text-rose-bright' : 'text-neutral-400',
          )}
        >
          <div>
            {copy.Next}: {nextIncomplete?.displayName}
          </div>
          <motion.div
            animate={{
              opacity: highlightNextIncomplete ? 1 : 0,
              y: highlightNextIncomplete ? 0 : -16,
              height: highlightNextIncomplete ? 24 : 0,
            }}
            transition={clampedTransition}
          >
            <ChevronDownIcon />
          </motion.div>
        </div>
      ) : (
        <div
          onClick={highlightNextIncomplete ? onNavigateBack : undefined}
          className={makeClassName(
            'flex h-16 flex-col items-center justify-center pt-2 font-medium ',
            highlightNextIncomplete ? 'text-rose-bright' : 'text-neutral-400',
          )}
        >
          <div>
            {highlightNextIncomplete
              ? copy.DoneTapHereToReturnHome
              : copy.LastCategory}
          </div>
          <motion.div
            animate={{
              opacity: highlightNextIncomplete ? 1 : 0,
              y: highlightNextIncomplete ? 0 : -16,
              height: highlightNextIncomplete ? 24 : 0,
            }}
            transition={clampedTransition}
          >
            <CheckCircleIcon />
          </motion.div>
        </div>
      )}
    </div>
  );
};
